import React, { useState } from 'react';
import { MonitorDown, X } from 'lucide-react';
import { Z_LAYERS } from '../constants/zLayers';
import { useInstallPrompt, promptInstall } from './installPrompt';

const DISMISS_KEY = 'tpl-install-pill-dismissed';

/**
 * Floating "Install app" pill for the PWA (ADR-012 Phase 4) — the button this
 * whole initiative exists for: it turns the web app into a taskbar/dock app.
 * Renders nothing when the app is already installed, the browser can't
 * install (e.g. Firefox), or the user dismissed it this browser.
 * On iOS Safari (no install-prompt API) it shows Add-to-Home-Screen help.
 */
export function InstallAppButton() {
  const state = useInstallPrompt();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [showIosHelp, setShowIosHelp] = useState(false);

  if (dismissed || state === 'installed' || state === 'unsupported') {
    return null;
  }

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, 'true');
    } catch {
      // Not persistable — it just reappears next visit.
    }
  };

  const onInstallClick = () => {
    if (state === 'ios') {
      setShowIosHelp(true);
    } else {
      void promptInstall();
    }
  };

  return (
    <div className="fixed bottom-4 right-4" style={{ zIndex: Z_LAYERS.TOAST }}>
      {showIosHelp && (
        <div className="mb-2 max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 text-sm text-gray-700 dark:text-gray-300">
          To install: tap the <span className="font-medium">Share</span> button in Safari, then
          choose <span className="font-medium">"Add to Home Screen"</span>.
        </div>
      )}
      <div className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg pl-4 pr-2 py-2">
        <button
          onClick={onInstallClick}
          className="flex items-center gap-2 text-sm font-medium focus:outline-none"
        >
          <MonitorDown className="w-4 h-4" aria-hidden="true" />
          Install app
        </button>
        <button
          onClick={dismiss}
          aria-label="Dismiss install suggestion"
          className="p-1 rounded-full hover:bg-indigo-500 focus:outline-none"
        >
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
